import React, { FC, useEffect } from "react";
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import * as TWEEN from '@tweenjs/tween.js';

import { getEulerAngles } from '../../utils/commonUtils';

export interface IArrowMeshProps {
  name?: string;
  position: THREE.Vector3;
  direction: THREE.Vector3;
  arrowLength: number;
  color: string;
  radius: number;
  layers: THREE.Layers;
};

const ArrowMesh = (props: IArrowMeshProps) => {
  const EXPANDED_LENGTH = 20;

  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);

  const [arrowLength, setArrowLength] = React.useState(props.arrowLength);
  const meshRef = React.useRef<THREE.Mesh>(null);
  const opacityRef = React.useRef(true);
  
  // Tween group to manage arrow expansion and contraction
  const tweenGroup = React.useRef(new TWEEN.Group()).current;

  // By default, the arrow points in the Y direction
  const rotEuler = getEulerAngles(new THREE.Vector3(0,1,0), props.direction, undefined);

  // Calculate the dimensions of the arrow based on length
  const dim = getArrowDimensions(arrowLength, props.radius);

  // Stable material: created once, never re-allocated on re-render.
  const arrowMaterialRef = React.useRef<THREE.MeshStandardMaterial | null>(null);
  if (arrowMaterialRef.current === null) {
    arrowMaterialRef.current = new THREE.MeshStandardMaterial({ color: props.color, transparent: true });
  }
  const arrowMaterial = arrowMaterialRef.current;

  // Sync material color when the prop changes without creating a new material.
  useEffect(() => {
    arrowMaterialRef.current!.color.set(props.color);
  }, [props.color]);

  // Release GPU resources on unmount.
  useEffect(() => {
    return () => {
      arrowMaterialRef.current?.dispose();
    };
  }, []);

  const onArrowClick = () => {
    setIsExpanded(!isExpanded)
    
     isExpanded 
      ? animateExpansion(EXPANDED_LENGTH, props.arrowLength)  
      : animateExpansion(props.arrowLength, EXPANDED_LENGTH);
  };
  
  // Animate the expansion and de-expansion of the arrow
  const animateExpansion = (from: number, to: number) => {

    const obj = { length: from };
    const easing = from < to ? TWEEN.Easing.Exponential.In : TWEEN.Easing.Exponential.Out;

    const tween = new TWEEN.Tween({ length: from }) // Start the tween with the value
      .to({ length: to }, 200)                // End at target value over 2 seconds
      .easing(easing) // Optional: add easing function
      .onUpdate((obj) => {
          setArrowLength(obj.length);
      })
      .start();
      tweenGroup.add(tween);
  };

  // The `useFrame` hook runs on every frame
  useFrame((state, delta) => {
    
    if (isHovering)
    {
      //opacityRef.current = arrowMaterial.opacity > 1 ? false : arrowMaterial.opacity < 0.8 ? true : opacityRef.current;
      //arrowMaterial.opacity += opacityRef.current ? 0.01 : -0.01;
      meshRef.current?.rotateOnAxis(new THREE.Vector3(0,1,0), 0.1);
    }
    
    tweenGroup.update(state.clock.elapsedTime*1000);

  });

  return (
      <mesh name={props.name} layers={props.layers} 
        ref={meshRef} 
        position={props.position} 
        rotation={rotEuler.toArray()} 
        onClick={() => onArrowClick()}
        onPointerOver={() => setIsHovering(true)}
        onPointerOut={() => setIsHovering(false)}
        >
        <mesh name="Tip" layers={props.layers} position={dim.cone.position} material={arrowMaterial}>
          <coneGeometry args={[dim.cone.radius, dim.cone.length, 5]} />
        </mesh>

        <mesh name="Shaft" layers={props.layers} position={dim.cylinder.position} material={arrowMaterial}>
          <cylinderGeometry args={[dim.cylinder.radius, dim.cylinder.radius, dim.cylinder.length]} />
        </mesh>
      </mesh>
  );
}

const getArrowDimensions = (length: number, radius: number)  => {
  const coneLength = length * 0.33;
  const cylinderLength = length - coneLength;

  const cylinderRadius = radius;
  const coneRadius = radius * 4;

  const conePosition: [number, number, number] = [0, cylinderLength + coneLength/2, 0];
  const cylinderPosition: [number, number, number] = [0, cylinderLength/2, 0];

  return { cone: {  position: conePosition, length: coneLength, radius: coneRadius }, 
          cylinder: { position: cylinderPosition, length: cylinderLength, radius: cylinderRadius } };
};

export default ArrowMesh;

